/**
 * Registry bootstrap checks for invariants TypeScript cannot express across entries
 * or between derived metadata and country definitions.
 *
 * We do not re-validate consent field shapes here: `LocaleConsent` and
 * `satisfies Record<LocaleCode, LocaleConsent>` in locale-consent-configs.ts
 * enforce those at authoring time. ResolvedLocale assembly trusts the frozen
 * registry without a second consent pass.
 */
import type { LocaleDefinition, LocaleRegistry } from "../types";

import { getCountryDefinition } from "../country/country-definitions";
import { deriveLocaleMetadata } from "../country/derive-locale-metadata";
import { parseLocaleCode } from "../country/parse-locale-code";
import { DEFAULT_LOCALE_CODE } from "./locale-registry-data";

function assertUniqueFieldValues(
  entries: LocaleDefinition[],
  getValue: (definition: LocaleDefinition) => string,
  fieldName: string,
): void {
  const seen = new Set<string>();

  for (const entry of entries) {
    const value = getValue(entry);

    if (seen.has(value)) {
      throw new Error(
        `Locale registry validation failed: duplicate ${fieldName} "${value}"`,
      );
    }

    seen.add(value);
  }
}

function validateDerivedFormatsMatchCountry(
  definition: LocaleDefinition,
): void {
  const { iso3166Alpha2Lower } = parseLocaleCode(definition.code);
  const metadata = deriveLocaleMetadata(definition.code);
  const countryDefinition = getCountryDefinition(iso3166Alpha2Lower);

  if (metadata.domain !== countryDefinition.domain) {
    throw new Error(
      `Locale registry validation failed: derived domain "${metadata.domain}" must be "${countryDefinition.domain}" (locale ${definition.code})`,
    );
  }

  if (metadata.canonicalOrigin !== `https://www.${countryDefinition.domain}`) {
    throw new Error(
      `Locale registry validation failed: derived canonicalOrigin "${metadata.canonicalOrigin}" is invalid (locale ${definition.code})`,
    );
  }
}

function validateLocaleRegistry(
  definitions: LocaleDefinition[],
): LocaleRegistry {
  if (definitions.length === 0) {
    throw new Error(
      "Locale registry validation failed: at least one locale is required",
    );
  }

  for (const definition of definitions) {
    // LocaleCode shape and consent payloads are compile-time checked; this
    // verifies derived URLs/domains stay aligned with country definitions.
    validateDerivedFormatsMatchCountry(definition);
  }

  assertUniqueFieldValues(definitions, (definition) => definition.code, "code");
  assertUniqueFieldValues(
    definitions,
    (definition) =>
      deriveLocaleMetadata(definition.code).country.iso3166Alpha3Lower,
    "iso3166Alpha3Lower",
  );
  assertUniqueFieldValues(
    definitions,
    (definition) => deriveLocaleMetadata(definition.code).domain,
    "domain",
  );

  if (
    !definitions.some((definition) => definition.code === DEFAULT_LOCALE_CODE)
  ) {
    throw new Error(
      `Locale registry validation failed: default locale "${DEFAULT_LOCALE_CODE}" is not registered`,
    );
  }

  return Object.freeze(
    Object.fromEntries(
      definitions.map((definition) => [definition.code, definition]),
    ) as LocaleRegistry,
  );
}

export { validateLocaleRegistry };
